import { useQuery } from '@tanstack/react-query';
import {
  ArchiveIcon,
  ArrowCounterClockwiseIcon,
  ArrowDownRightIcon,
  ArrowsLeftRightIcon,
  ArrowUUpLeftIcon,
  CheckCircleIcon,
  CircleIcon,
  type Icon,
  NotePencilIcon,
  PlayCircleIcon,
  ProhibitIcon,
  RowsPlusBottomIcon,
  UserPlusIcon,
  WarningOctagonIcon,
} from '@phosphor-icons/react';
import type { AuditEntry, CardId } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { boardRepository } from '@web/data/repositories';
import { currentLocale, t } from '@web/i18n';
import { useOrgContext } from '@web/ui/core';
import { twcn } from '@web/ui/core/utils';

import { type HistoryIcon, type HistoryTone, presentAuditEntry } from './history_presenter';

export interface HistoryViewProps {
  cardId: CardId;
}

/**
 * History tab. Reads the card audit log and renders it as a timeline:
 * a tone-tinted icon badge, the event title, a dot-separated metadata
 * subtitle (best-effort from the audit `changeset`) and a stacked
 * date/time on the right. The kanban service exposes
 * `GET /orgs/:orgId/cards/:cardId/audit`.
 */
export function HistoryView({ cardId }: Readonly<HistoryViewProps>): React.JSX.Element {
  const { orgId } = useOrgContext();

  const query = useQuery({
    queryKey: queryKeys.cards.audit(orgId, cardId),
    queryFn: () => boardRepository.loadCardAudit(orgId, cardId),
  });

  if (query.isLoading) {
    return <p className="text-meta text-muted-foreground">{t('board.drawer.history.loading')}</p>;
  }
  if (query.isError || !query.data) {
    return <p className="text-meta text-tone-red-fg">{t('board.drawer.history.error')}</p>;
  }
  if (query.data.length === 0) {
    return (
      <p
        data-testid="drawer-history-empty"
        className="text-meta text-faint-foreground text-center py-6"
      >
        {t('board.drawer.history.empty')}
      </p>
    );
  }

  // Newest first — the timeline metaphor flips the chronological order so the
  // most recent event sits on top.
  const entries = [...query.data].sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
  );

  return (
    <ol data-testid="drawer-history-list" className="flex flex-col list-none m-0 p-0">
      {entries.map((entry) => (
        <HistoryRow key={entry.id} entry={entry} />
      ))}
    </ol>
  );
}

const HISTORY_ICONS: Record<HistoryIcon, Icon> = {
  archive: ArchiveIcon,
  'user-plus': UserPlusIcon,
  'arrow-down-right': ArrowDownRightIcon,
  'arrow-u-left': ArrowUUpLeftIcon,
  'check-circle': CheckCircleIcon,
  'rows-plus': RowsPlusBottomIcon,
  'arrows-left-right': ArrowsLeftRightIcon,
  'play-circle': PlayCircleIcon,
  prohibit: ProhibitIcon,
  'arrow-counter-clockwise': ArrowCounterClockwiseIcon,
  warning: WarningOctagonIcon,
  'note-pencil': NotePencilIcon,
  circle: CircleIcon,
};

// Tone tint/ink for the round icon badge. Mirrors the tone token classes
// used by ToneChip, but ToneChip is a label pill (not an icon-only circle),
// so its private class table is not reusable here.
const TONE_BADGE: Record<HistoryTone, string> = {
  green: 'bg-tone-green-tint text-tone-green-ink',
  amber: 'bg-tone-amber-tint text-tone-amber-ink',
  red: 'bg-tone-red-tint text-tone-red-ink',
  gray: 'bg-tone-gray-tint text-tone-gray-ink',
  ai: 'bg-ai-tint text-ai-ink',
};

function HistoryRow({ entry }: Readonly<{ entry: AuditEntry }>): React.JSX.Element {
  const locale = currentLocale();
  const view = presentAuditEntry(entry, locale);
  const Cmp = HISTORY_ICONS[view.icon];
  const when = new Date(entry.at);
  const time = when.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const date = when.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
  return (
    <li className="flex items-start gap-3 py-2 border-b border-border-soft last:border-b-0">
      <span
        aria-hidden
        className={twcn(
          'mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-pill',
          TONE_BADGE[view.tone],
        )}
      >
        <Cmp size={14} weight="regular" />
      </span>
      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
        <span className="text-base font-medium text-foreground truncate">{view.title}</span>
        {view.meta.length > 0 ? (
          <span className="text-meta text-muted-foreground truncate">{view.meta.join(' · ')}</span>
        ) : null}
      </div>
      <span className="flex flex-col items-end shrink-0 pt-0.5 font-mono text-card-id text-muted-foreground tabular-nums whitespace-nowrap">
        <span className="uppercase tracking-eyebrow">{date}</span>
        <span>{time}</span>
      </span>
    </li>
  );
}
